import React from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { type TemplateOption, nudgeNoteTypesFor, useTemplateOptions } from "../../hooks/useTemplateOptions";
import { getRecentNoteTypes } from "../../service/notes.service";
import { type SchedulePick, type ScheduleSlot, useGetTodaySchedulePickQuery } from "../../service/schedulePick.service";
import { cacheClients } from "../../slices/clientSlice";
import { EventType, trackEvent } from "../../utils/analytics";
import { eventEmitter } from "../../utils/eventEmitter";
import { readStoredTemplateChoice, rememberTemplateChoice, resolveSessionTemplate } from "../../utils/sessionTemplate";
import { type QuickStartView, loadDismissed, quickStartView, saveDismissed, upcomingSlots } from "./quickStart";

// The slot chip and "who is next" drift with the clock; the sheet itself only changes when a
// note lands (refetched on that event) or a pull runs.
const SCHEDULE_POLL_MS = 5 * 60 * 1000;

export type QuickStartSchedule = {
  /** The card asks for today's sheet (everywhere but the demo). */
  enabled: boolean;
  /**
   * The account has EHR scheduling at all: it can pull one, receives one by share, or a pull
   * has stamped it. Decides whether "no appointments today" is worth saying.
   */
  hasEhrSchedule: boolean;
  pick: SchedulePick | null;
  /** What the card offers; see ``quickStartView``. */
  view: QuickStartView;
  /** Every undocumented appointment left today, in the server's order (See all). */
  upcoming: ScheduleSlot[];
  /** Put a patient from See all on the card. */
  choose: (clientId: number) => void;
  /** × : turn this appointment's Quick Start down; Start Encounter until the schedule moves on. */
  dismiss: () => void;
  /** Undo the ×. */
  restore: () => void;
};

/**
 * Today's sheet for the Start card. Reads the same ranked sheet as the Start Encounter
 * drawer (``GET /client/schedule/today/``), so the card and the drawer agree on who is
 * next; one subscription is shared by every mounted card.
 */
export const useQuickStartSchedule = (): QuickStartSchedule => {
  const dispatch = useAppDispatch();
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const { isDemoPath } = useAppSelector((state) => state.appState);
  // Not gated on can_sync_ehr_patients: that is "may press Sync", and an account whose day
  // sheet arrives by the nightly pull alone (a NextGen lane, say) has a schedule without it.
  // The schedule endpoint says whether there is one; Quick Start's flag is the opt-in.
  const enabled = !isDemoPath;

  const { data, refetch } = useGetTodaySchedulePickQuery(
    {},
    { skip: !enabled, pollingInterval: SCHEDULE_POLL_MS, refetchOnFocus: true, refetchOnMountOrArgChange: true },
  );
  const pick = enabled ? data ?? null : null;

  const [chosenId, setChosenId] = React.useState<number | null>(null);
  const [dismissedId, setDismissedId] = React.useState<number | null>(null);

  React.useEffect(() => {
    setDismissedId(loadDismissed(pick?.day));
  }, [pick?.day]);

  // Rows carry full client objects; caching them lets the recording header and the
  // client page resolve the patient without another fetch.
  React.useEffect(() => {
    if (!pick) return;
    const clients = upcomingSlots(pick).map((slot) => slot.client);
    if (clients.length) dispatch(cacheClients(clients));
  }, [pick, dispatch]);

  // A note that starts or lands moves its patient to "documented": re-rank right away.
  React.useEffect(() => {
    if (!enabled) return;
    const onNotesChanged = () => {
      refetch();
    };
    eventEmitter.on("noteCreated", onNotesChanged);
    eventEmitter.on("refreshNotes", onNotesChanged);
    return () => {
      eventEmitter.off("noteCreated", onNotesChanged);
      eventEmitter.off("refreshNotes", onNotesChanged);
    };
  }, [enabled, refetch]);

  const upcoming = React.useMemo(() => upcomingSlots(pick), [pick]);
  const view = React.useMemo(() => quickStartView(pick, { chosenId, dismissedId }), [pick, chosenId, dismissedId]);

  const choose = React.useCallback(
    (clientId: number) => {
      const rank = upcoming.findIndex((slot) => slot.client.id === clientId);
      trackEvent(EventType.QUICK_START_PATIENT_SWITCHED, {
        from_view: view.kind,
        to_rank: rank >= 0 ? rank + 1 : null,
        was_suggestion: pick?.suggestion?.client_id === clientId,
      });
      setChosenId(clientId);
    },
    [upcoming, view.kind, pick?.suggestion?.client_id],
  );

  const dismiss = React.useCallback(() => {
    const id = upcoming[0]?.client.id;
    if (id == null || !pick) return;
    trackEvent(EventType.QUICK_START_DISMISSED, {
      view: view.kind,
      candidates: view.kind === "choose" ? view.slots.length + view.more : 1,
      confidence: pick.suggestion?.confidence ?? null,
    });
    saveDismissed(pick.day, id);
    setDismissedId(id);
    setChosenId(null);
  }, [upcoming, pick, view]);

  const restore = React.useCallback(() => {
    if (pick) saveDismissed(pick.day, null);
    setDismissedId(null);
    trackEvent(EventType.QUICK_START_RESTORED);
  }, [pick]);

  const hasEhrSchedule =
    userInfo?.can_sync_ehr_patients === true || userInfo?.can_view_ehr_schedule === true || !!pick?.synced_at;

  return { enabled, hasEhrSchedule, pick, view, upcoming, choose, dismiss, restore };
};

// Both mounted cards ask at once on load; share that one request.
let recentInFlight: Promise<number[]> | null = null;
const fetchRecentNoteTypes = (): Promise<number[]> => {
  if (!recentInFlight) {
    recentInFlight = getRecentNoteTypes()
      .then((response) => response.data.note_types ?? [])
      .finally(() => {
        recentInFlight = null;
      });
  }
  return recentInFlight;
};

export type SessionTemplateState = ReturnType<typeof useSessionTemplate>;

/**
 * The template a Quick Start will use, named before the clinician presses it.
 * See ``resolveSessionTemplate`` for the order of evidence.
 */
export const useSessionTemplate = () => {
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const { isCreateModalVisible } = useAppSelector((state) => state.appState);
  const noteTypes = React.useMemo(() => nudgeNoteTypesFor(userInfo), [userInfo]);
  const { groups, options, customNotesLoaded, loadGallery } = useTemplateOptions({ noteTypes, galleryOnMount: false });
  const galleryLoaded = useAppSelector((state) => state.appState.templates.length > 0);
  const [recent, setRecent] = React.useState<number[] | null>(null);
  const [stored, setStored] = React.useState<number | null>(() => readStoredTemplateChoice());
  const [chosen, setChosen] = React.useState<number | string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetchRecentNoteTypes()
      .then((noteTypes) => {
        if (!cancelled) setRecent(noteTypes);
      })
      .catch((error) => {
        console.error("Could not load recent templates:", error);
        if (!cancelled) setRecent([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The drawer may have just remembered a different template; read it again when it closes.
  React.useEffect(() => {
    if (!isCreateModalVisible) setStored(readStoredTemplateChoice());
  }, [isCreateModalVisible]);

  const template = React.useMemo(
    () => resolveSessionTemplate({ options, chosen, stored, recent }),
    [options, chosen, stored, recent],
  );

  const choose = React.useCallback(
    (option: TemplateOption) => {
      rememberTemplateChoice(option.value, Boolean(option.is_template));
      setStored(readStoredTemplateChoice());
      setChosen(option.value);
      trackEvent(EventType.QUICK_START_TEMPLATE_CHANGED, {
        group: option.group,
        had_template: template !== null,
        from_source: template?.source ?? null,
      });
    },
    [template],
  );

  return {
    template,
    /** False until both the picker's options and the recent history have loaded. */
    ready: customNotesLoaded && recent !== null,
    groups,
    options,
    choose,
    /** The gallery is only fetched once the picker is about to show it. */
    loadGallery: galleryLoaded ? () => {} : loadGallery,
  };
};
